import Konva from "konva";
import { GenericNodeEvents } from "./common";
import type { StandState2 } from "./types";

export type StandOptions = (
  | {
      shape: "circle";
      shapeOptions: Konva.CircleConfig;
    }
  | { shape: "rect"; shapeOptions: Konva.RectConfig }
) & {
  label: string;
  stageLayer: Konva.Layer;
};

export class Stand2 extends GenericNodeEvents<Stand2, StandState2> {
  private id!: string;
  private readonly layer: Konva.Layer;
  private container: Konva.Shape;
  private standGroup: Konva.Group;
  private label!: Konva.Text;
  private state: StandState2;
  private image?: Konva.Image;

  get nodeId() {
    return this.id;
  }

  get currentState() {
    return this.state;
  }

  constructor(layer: Konva.Layer, state: StandState2) {
    const group = new Konva.Group({
      draggable: state.draggable !== undefined ? state.draggable : true,
    });

    super(group, "stand");
    this.target = this;
    this.state = state;

    this.layer = layer;
    this.container = new Konva.Rect();
    this.standGroup = group;
    this.layer.add(this.standGroup);
    this.standGroup.add(this.container);
  }

  create() {
    const state = this.state;
    this.standGroup.destroyChildren();
    this.initElements(state);

    this.standGroup.on("transformend", () => {
      this.state.position = this.standGroup.position();
      this.state.rotation = this.standGroup.rotation();
      this.state.scaleX = this.standGroup.scaleX();
      this.state.scaleY = this.standGroup.scaleY();

      if (this.onStateChangeCallback) {
        this.onStateChangeCallback(this.state);
      }
    });

    this.standGroup.on("transform", () => {
      const containerWidth = this.standGroup.scaleX() * this.container.width();
      const textWidth = this.standGroup.scaleX() * this.label.width();

      this.container.setAttr("width", containerWidth);

      this.label.setAttr("width", textWidth);

      if (this.state.shape === "rect") {
        this.state.width = containerWidth;
        this.state.textWidth = textWidth;
      }
    });

    this.id = state.id;
    this.standGroup.setAttrs({
      ...state.position,
      rotation: this.state.rotation,
      scaleX: this.state.scaleX || 1,
      scaleY: this.state.scaleY || 1,
    });
  }

  update() {
    this.standGroup.destroyChildren();
    const state = this.state;
    this.initElements(state);

    this.id = state.id;
    this.standGroup.setAttrs({
      ...state.position,
    });
    this.setupLabel(state.label);

    if (this.onStateChangeCallback) {
      this.onStateChangeCallback(this.state);
    }
  }

  private initElements(state: StandState2) {
    if (state.shape === "rect") {
      this.container = new Konva.Rect({
        fill: state.background,
        height: state.height,
        width: state.width,
        strokeWidth: state.strokeWidth ?? 3,
        stroke: this.state.color,
      });
    } else {
      this.container = new Konva.Circle({
        fill: state.background,
        radius: state.radius,
      });
    }

    if (this.state.imageUrl) {
      Konva.Image.fromURL(
        this.state.imageUrl,
        (image: any) => {
          this.image = image;
          this.standGroup.add(image);
        },
        (error: any) => {
          console.log(error);
        },
      );
    }

    this.setupLabel(state.label);
    this.standGroup.add(this.container, this.label);
    this.standGroup.moveToBottom();
  }

  private setupLabel(label: string) {
    this.label = new Konva.Text({
      text: label,
      fontSize: this.state.fontSize || 20,
      fontFamily: "Geist Mono",
      fontStyle: "bold",
      fill: this.state.color,
      x: this.standGroup.x(),
      y: this.standGroup.y(),
      width: this.container.width(),
      height: this.container.height(),
      align: "center",
      verticalAlign: "middle",
    });
  }
}
